import { fileStorageApi, dataAssetsApi, SAIFTY_APIS_BASE_URL, getMonolithAPIsByBaseURL, organizationsApi } from '@/api'
import { DocumentMetaData } from '@coac-gmbh/library-api/models'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useToasts from './useToasts'
import { extension } from 'mime-types'
import { read, writeFile } from 'xlsx'
import { extension as getExtension } from 'mime-types'
import axiosInstance from '@/api/axios'
import { DataObjectDTO } from '@coac-gmbh/saifty-main-apis'

export declare enum LibraryExportTypes {
  XLSX = 'xlsx',
  IDOC = 'idoc',
  XML = 'xml',
}

/* REUSABLE STUFF RELATED TO BACK OFFICE AND DATA ASSETS/OBJECTS DOCUMENTS/FILES */

export default function useDownloadDocuments() {
  const downloadingSource = ref(false)
  const { addToast } = useToasts()
  const { t } = useI18n()

  async function handleDownloadSourcePDF(dataObject: DataObjectDTO) {
    if (
      dataObject.copiedFromInstnaceId &&
      dataObject.sourceDataAssetId &&
      dataObject.sourceDataObjectId &&
      dataObject.documentId
    ) {
      downloadSourcePDFFromAnotherInstance(
        dataObject.copiedFromInstnaceId,
        dataObject.sourceDataAssetId,
        dataObject.sourceDataObjectId,
        dataObject.documentId,
      )
    } else if (dataObject.documentId) {
      downloadSourcePDF(dataObject.documentId)
    }
  }

  async function downloadSourcePDFFromAnotherInstance(
    instanceId: string,
    sourceDataAssetId: string,
    sourceDataObjectId: string,
    documentId: string,
  ) {
    try {
      const { data: blob } = await organizationsApi.downloadDataObjectDocumentSharedToInstanceRequest(
        {
          dataAssetId: sourceDataAssetId,
          dataObjectId: sourceDataObjectId,
          instanceId: instanceId,
        },
        { responseType: 'blob' },
      )

      downloadBlob(`${documentId}.pdf`, blob)
    } catch {
      addToast({ text: t('Error downloading the file'), variant: 'danger' })
    }
  }

  async function downloadSourcePDF(documentId: string) {
    downloadingSource.value = true
    try {
      const { data: blob } = await fileStorageApi.getFile({ fileId: documentId }, { responseType: 'blob' })

      downloadBlob(`${documentId}.pdf`, blob)
    } catch {
      addToast({ text: t('Error downloading the file'), variant: 'danger' })
    }
    downloadingSource.value = false
  }

  async function downloadFileFromFilestorage(fileId: string, fileName?: string) {
    downloadingSource.value = true
    try {
      const { data: blob } = await fileStorageApi.getFile({ fileId: fileId }, { responseType: 'blob' })
      let fileNameToUse = fileName
      if (!fileNameToUse) {
        const { data: fileData } = await fileStorageApi.getFileData({ fileId: fileId })
        fileNameToUse = fileData.fileName
      }

      downloadBlob(fileNameToUse || fileId, blob)
    } catch {
      addToast({ text: t('Error downloading file'), variant: 'danger' })
    }
    downloadingSource.value = false
  }

  async function downloadPublicSourcePDF(dataObject: DataObjectDTO) {
    if (!dataObject.documentId) {
      return
    }

    downloadingSource.value = true
    try {
      const { data: blob } = await dataAssetsApi.downloadDataObjectDocument(
        {
          dataAssetId: dataObject.dataAssetsId,
          dataObjectId: dataObject.id,
          documentId: dataObject.documentId,
          source: 'documentId',
        },
        { responseType: 'blob' },
      )

      downloadBlob(`${dataObject.documentId}.pdf`, blob)
    } catch {
      addToast({ text: t('Error to download the file'), variant: 'danger' })
    }
    downloadingSource.value = false
  }

  async function downloadDataObject(fileName: string, dataAssetId: string, dataObjectId: string, url?: string) {
    downloadingSource.value = true
    try {
      const { data: blob } = url
        ? await getMonolithAPIsByBaseURL(url).downloadDataObject(
            {
              dataAssetsId: dataAssetId,
              dataObjectId: dataObjectId,
            },
            { responseType: 'blob' },
          )
        : await dataAssetsApi.downloadDataObject(
            {
              dataAssetsId: dataAssetId,
              dataObjectId: dataObjectId,
            },
            { responseType: 'blob' },
          )

      downloadBlob(`${fileName || 'data-object'}.xlsx`, blob)
    } catch {
      addToast({ text: t('Error to download the file'), variant: 'danger' })
    }
    downloadingSource.value = false
  }

  async function downloadDataAsset(fileName: string, dataAssetId: string) {
    downloadingSource.value = true

    try {
      const { data: blob, headers } = await axiosInstance.get('/dataassets/download/v2', {
        baseURL: SAIFTY_APIS_BASE_URL,
        params: { dataAssetId },
        responseType: 'blob',
      })

      downloadBlob(`${fileName || 'data-asset'}.${extension(headers['content-type'])}`, blob)
    } catch {
      addToast({ text: t('Error to download the file'), variant: 'danger' })
    }
    downloadingSource.value = false
  }

  async function downloadDataAssetHazardSubstance(fileName: string, dataAssetId: string) {
    downloadingSource.value = true
    try {
      const { data: blob, headers } = await dataAssetsApi.downloadDataAssetsHazardSubstances(
        { dataAssetId },
        { responseType: 'blob' },
      )

      const contentType = headers['content-type'] || ''
      const extension = getExtension(contentType) || 'xlsx'

      downloadBlob(`${fileName || 'data-asset-hazard-substance'}.${extension}`, blob)
    } catch {
      addToast({ text: t('Error to download the file'), variant: 'danger' })
    }
    downloadingSource.value = false
  }

  async function downloadLibraryDocument(document: DocumentMetaData) {
    if (document.documentId) {
      try {
        const { data: documentData } = await fileStorageApi.getFileData({ fileId: document.documentId })
        const { data: blob } = await fileStorageApi.getFile({ fileId: document.documentId }, { responseType: 'blob' })
        if (documentData.mimeType) {
          let filename = `${document.documentId || document.materialNumber || 'document'}.${extension(
            documentData.mimeType,
          )}`
          if (documentData.fileName?.includes('.')) {
            filename = documentData.fileName // if the filename already has an extension, use it
          }
          downloadBlob(filename, blob)
        }
      } catch {
        addToast({ text: t('Error downloading the file'), variant: 'danger' })
      }
    } else {
      addToast({ text: 'The document does not have Id' })
    }
  }

  async function downloadObjectTemplateXLSX() {
    try {
      const blob = await fetch('/files/PassportItemsUploadTemplate.xlsx').then((response) => response.blob())
      downloadBlob('Passport Items Template.xlsx', blob)
    } catch {
      addToast({ text: t('Error downloading the file'), variant: 'danger' })
    }
  }

  async function downloadDataLookupXLSXTemplate() {
    try {
      const blob = await fetch('/files/Data Lookup Template.xlsx').then((response) => response.blob())
      downloadBlob('Data Lookup Template.xlsx', blob)
    } catch {
      addToast({ text: t('Error downloading the file'), variant: 'danger' })
    }
  }

  async function downloadAssessmentXLSXTemplate() {
    try {
      const blob = await fetch('/files/Assessment Template.xlsx').then((response) => response.blob())
      downloadBlob('Assessment Template.xlsx', blob)
    } catch {
      addToast({ text: t('Error downloading the file'), variant: 'danger' })
    }
  }

  async function downloadCreateAssetDataObjectsXLSXTemplate() {
    try {
      const blob = await fetch('/files/Create-asset-data-objects-template.xlsx').then((response) => response.blob())
      downloadBlob('Create-asset-data-objects-template.xlsx', blob)
    } catch {
      addToast({ text: t('Error downloading the file'), variant: 'danger' })
    }
  }

  async function downloadCVSFromFilestorageAsXLSX(fileId: string) {
    downloadingSource.value = true
    try {
      const { data: file } = await fileStorageApi.getFile({ fileId: fileId }, { responseType: 'arraybuffer' })
      const { data: fileData } = await fileStorageApi.getFileData({ fileId: fileId })
      const workbook = read(file)
      writeFile(workbook, `${fileData.fileName || fileId}.xlsx`, { compression: true })
    } catch {
      addToast({ text: t('Error downloading file'), variant: 'danger' })
    }
    downloadingSource.value = false
  }

  async function downloadLocalDocumentByName(fileName: string) {
    downloadingSource.value = true
    try {
      const blob = await fetch(`/files/${fileName}`).then((response) => response.blob())
      downloadBlob(fileName, blob)
    } catch {
      addToast({ text: t('Error downloading the file'), variant: 'danger' })
    }
    downloadingSource.value = false
  }

  function downloadBlob(title: string, data: Blob) {
    const a = document.createElement('a')
    a.href = window.URL.createObjectURL(data)
    a.download = title
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    addToast({ text: t('Download Ready'), variant: 'success' })
  }

  return {
    downloadingSource,
    downloadSourcePDF,
    downloadFileFromFilestorage,
    downloadPublicSourcePDF,
    downloadDataObject,
    downloadDataAsset,
    downloadDataAssetHazardSubstance,
    downloadLibraryDocument,
    downloadObjectTemplateXLSX,
    downloadAssessmentXLSXTemplate,
    downloadCVSFromFilestorageAsXLSX,
    downloadDataLookupXLSXTemplate,
    downloadCreateAssetDataObjectsXLSXTemplate,
    downloadLocalDocumentByName,
    handleDownloadSourcePDF,
    downloadBlob,
  }
}
